/** The fields pill (`header.pills.fields`, deliverable 18): how many fields are filled, one click from editing them. */
import type { SlotComponentProps } from '@codex/contracts';
import { Button, Skeleton } from '@codex/ui';
import { useState } from 'react';
import { applicableFields, filledCount, refsOf } from '../model/values.js';
import { useDocFields } from './doc-fields.js';
import { FieldsDialog } from './FieldsDialog.js';
import '../types.css';
import './pills.css';

/** "Fields" for none, "1 field", "<n> fields". */
export function fieldsLabel(filled: number): string {
  if (filled === 0) return 'Fields';
  return filled === 1 ? '1 field' : `${filled} fields`;
}

export function FieldsPill({ docId }: SlotComponentProps) {
  const { doc, links, types, linkTypes, loading, error, refreshDoc, reload } = useDocFields(docId);
  const [open, setOpen] = useState(false);
  if (error && !doc)
    return (
      <Button size="sm" onClick={reload}>
        Try again
      </Button>
    );
  if (loading)
    return (
      <span className="cx-pills__skeleton">
        <Skeleton lines={1} label="Loading fields" />
      </span>
    );
  const type = doc ? types.find((t) => t.id === doc.typeId) : undefined;
  if (!doc || !type) return null;
  const fields = applicableFields(type, types);
  if (fields.length === 0) return null;
  const filled = filledCount(
    fields,
    doc.props,
    (field) => refsOf(field, doc.id, links, linkTypes).length,
  );
  const readOnly = doc.library === 'reference' || doc.deletedAt !== null;
  return (
    <>
      <Button size="sm" aria-haspopup="dialog" onClick={() => setOpen(true)}>
        {fieldsLabel(filled)}
      </Button>
      {open && (
        <FieldsDialog
          doc={doc}
          fields={fields}
          links={links}
          linkTypes={linkTypes}
          readOnly={readOnly}
          onClose={() => setOpen(false)}
          refreshDoc={refreshDoc}
          reload={reload}
        />
      )}
    </>
  );
}
